import React from 'react';
import { CalendarDays, Clock, ChevronRight } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Card, CardContent } from '../ui/card';
import { useAppointmentBooking } from '../../hooks/useAppointmentBooking';
import { SlotPicker } from './SlotPicker';
import { AppointmentConfirmation } from './AppointmentConfirmation';

const VISIT_TYPES = [
  { value: 'new-patient', label: 'New Patient Evaluation' },
  { value: 'follow-up', label: 'Follow-up Visit' },
  { value: 'telehealth', label: 'Telehealth Visit' },
];

const maxDate = () => {
  const d = new Date();
  d.setDate(d.getDate() + 60);
  return d.toISOString().slice(0, 10);
};

export const AppointmentBooking = () => {
  const {
    selectedDate, setSelectedDate, availability, loadingSlots,
    selectedTime, setSelectedTime, details, handleDetailChange,
    submitting, confirmation, bookAppointment, resetBooking,
  } = useAppointmentBooking();

  return (
    <section id="appointment" data-testid="appointment-booking-section" className="fade-in-section py-12 md:py-20 px-4 relative z-10">
      <div className="container mx-auto">
        <div className="text-center mb-8 md:mb-12">
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-darkBrown mb-4">Book Your Appointment</h2>
          <p className="text-sm md:text-lg text-gray-600">Choose a time that works for you — weekdays, 9:00 AM to 6:00 PM</p>
          <div className="w-24 h-1 bg-primaryOrange mx-auto mt-4"></div>
        </div>

        {confirmation ? (
          <AppointmentConfirmation appointment={confirmation} onReset={resetBooking} />
        ) : (
          <Card className="border-none shadow-xl max-w-5xl mx-auto bg-white">
            <CardContent className="p-5 md:p-8">
              <form onSubmit={bookAppointment} className="grid md:grid-cols-2 gap-8">
                <div className="space-y-5">
                  <div>
                    <Label htmlFor="appt-date" className="flex items-center text-darkBrown font-semibold mb-2">
                      <CalendarDays size={16} className="mr-2 text-primaryOrange" /> Select a date
                    </Label>
                    <Input
                      id="appt-date"
                      data-testid="appointment-date-input"
                      type="date"
                      value={selectedDate}
                      min={new Date().toISOString().slice(0, 10)}
                      max={maxDate()}
                      onChange={(e) => setSelectedDate(e.target.value)}
                      required
                    />
                    {availability?.isOpen && (
                      <p className="text-xs text-gray-500 mt-2" data-testid="availability-label">{availability.dateLabel}</p>
                    )}
                  </div>

                  <div>
                    <Label className="flex items-center text-darkBrown font-semibold mb-2">
                      <Clock size={16} className="mr-2 text-primaryOrange" /> Available times
                    </Label>
                    <SlotPicker
                      availability={availability}
                      loading={loadingSlots}
                      selectedTime={selectedTime}
                      onSelect={setSelectedTime}
                    />
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <Label htmlFor="appt-name" className="text-darkBrown font-semibold">Full name</Label>
                    <Input id="appt-name" data-testid="appointment-name-input" name="name" value={details.name} onChange={handleDetailChange} placeholder="Jane Doe" required className="mt-1.5" />
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="appt-email" className="text-darkBrown font-semibold">Email</Label>
                      <Input id="appt-email" data-testid="appointment-email-input" name="email" type="email" value={details.email} onChange={handleDetailChange} placeholder="you@email.com" required className="mt-1.5" />
                    </div>
                    <div>
                      <Label htmlFor="appt-phone" className="text-darkBrown font-semibold">Phone</Label>
                      <Input id="appt-phone" data-testid="appointment-phone-input" name="phone" value={details.phone} onChange={handleDetailChange} placeholder="(281) 555-0123" required className="mt-1.5" />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="appt-visit-type" className="text-darkBrown font-semibold">Visit type</Label>
                    <select
                      id="appt-visit-type"
                      data-testid="appointment-visit-type-select"
                      name="visitType"
                      value={details.visitType}
                      onChange={handleDetailChange}
                      className="mt-1.5 w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
                    >
                      {VISIT_TYPES.map((v) => <option key={v.value} value={v.value}>{v.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <Label htmlFor="appt-reason" className="text-darkBrown font-semibold">Reason for visit (optional)</Label>
                    <Textarea id="appt-reason" data-testid="appointment-reason-input" name="reason" value={details.reason} onChange={handleDetailChange} rows={3} placeholder="Briefly tell us what brings you in" className="mt-1.5" />
                  </div>

                  <div className="rounded-md bg-warmBeige/70 px-4 py-3 text-xs md:text-sm text-gray-700" data-testid="appointment-selection-summary">
                    {selectedTime
                      ? `Selected: ${availability?.dateLabel} at ${availability?.slots.find((s) => s.time === selectedTime)?.label}`
                      : 'Pick a time slot on the left to continue.'}
                  </div>

                  <Button
                    type="submit"
                    data-testid="appointment-submit-btn"
                    disabled={submitting || !selectedTime}
                    className="w-full bg-primaryOrange hover:bg-accentOrange text-white shadow-lg hover:shadow-xl transition-all"
                    size="lg"
                  >
                    {submitting ? 'Reserving...' : 'Reserve This Time'}
                    {!submitting && <ChevronRight className="ml-2" size={18} />}
                  </Button>
                  <p className="text-[11px] md:text-xs text-gray-500 text-center">
                    Reservations are confirmed by our staff by phone. For emergencies, call 988 or 911.
                  </p>
                </div>
              </form>
            </CardContent>
          </Card>
        )}
      </div>
    </section>
  );
};
